import React, { useState } from 'react';
import { Upload, Button, Alert, Progress } from 'antd';
import { UploadOutlined, FileExcelOutlined } from '@ant-design/icons';

const FileUploader = ({ 
  onParse,
  accepted = '.xlsx,.xls,.csv',
  maxSize = 10, // MB
  title = 'Upload File',
  description = 'Select an Excel or CSV file to upload',
  showProgress = true,
  disabled = false,
  ...props 
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleUpload = async (file) => {
    setError(null);
    setSuccess(null);
    setUploading(true);
    setProgress(0);

    try {
      // Validate file type
      const validExtensions = accepted.split(',').map(ext => ext.trim().toLowerCase());
      const fileExtension = '.' + file.name.toLowerCase().split('.').pop();
      
      if (!validExtensions.includes(fileExtension)) {
        throw new Error(`Invalid file type. Please upload a file with one of these extensions: ${accepted}`);
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSize) {
        throw new Error(`File size too large. Maximum size is ${maxSize}MB.`);
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Parse the file
      const result = await onParse(file);
      
      clearInterval(progressInterval);
      setProgress(100);
      
      // Show success message
      const recordCount = Array.isArray(result) ? result.length : result?.length || 0;
      setSuccess(`Successfully uploaded and parsed ${recordCount} records from ${file.name}`);
      
    } catch (err) {
      console.error('File upload error:', err);
      setError(err.message || 'Failed to parse file');
      setProgress(0);
    } finally {
      setUploading(false);
      
      // Clear messages after delay
      setTimeout(() => {
        setError(null);
        setSuccess(null);
        setProgress(0);
      }, 5000);
    }

    return false; // Prevent default upload behavior
  };

  const uploadProps = {
    accept: accepted,
    beforeUpload: handleUpload,
    showUploadList: false,
    disabled: disabled || uploading,
    ...props
  };

  return (
    <div className="file-uploader">
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ margin: 0, marginBottom: 8 }}>{title}</h4>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>{description}</p>
      </div>
      
      <Upload {...uploadProps}>
        <Button 
          icon={<UploadOutlined />} 
          loading={uploading}
          disabled={disabled || uploading}
          size="large"
        >
          {uploading ? 'Processing...' : 'Select File'}
        </Button>
      </Upload>
      
      {showProgress && uploading && progress > 0 && (
        <div style={{ marginTop: 16 }}>
          <Progress 
            percent={progress} 
            status={progress === 100 ? 'success' : 'active'}
            showInfo={true}
          />
        </div>
      )}
      
      {error && (
        <Alert
          message="Upload Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginTop: 16 }}
          onClose={() => setError(null)}
        />
      )}
      
      {success && (
        <Alert
          message="Upload Successful"
          description={success}
          type="success"
          showIcon
          closable
          style={{ marginTop: 16 }}
          onClose={() => setSuccess(null)}
          icon={<FileExcelOutlined />}
        />
      )}
    </div>
  );
};

export default FileUploader;
